import { createStore as _createStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import type { ParcelFilters, ParcelInfo, ParcelVisualization } from "../../parcels";
import { VisibleParcels } from "../../parcels";
import type { PmTilesSource, PmTilesSourceId } from "./PmTilesService/types";
import type { MapCameraProjection, WmsSource, WmsSourceId } from "./types";

type MapStoreState = {
	camera: {
		bearing: number; // degrees
		pitch: number; // degrees
		scaleDenominator: number; // the N in "1:N", for the map centre
		projection: MapCameraProjection;
	};
	sources: {
		wms: Map<WmsSourceId, WmsSource>;
		pmTiles: Map<PmTilesSourceId, PmTilesSource>;
	};
	layers: {
		buildings3d: boolean; // the "3d-buildings" fill-extrusion layer
		parcelLabels: boolean; // the parcel-number symbol layer
	};
	parcels: {
		// data only, visualization handled by maplibre-connector module
		filters: ParcelFilters;
		visualization: ParcelVisualization; // which field drives parcel height / colour on the map
		visibleParcels: VisibleParcels; // for the list (UI) of parcels currently visible in the map viewport
		hoveredParcelId: string | null; // two-way binding with the map hover state (Map <-> UI)
		activeParcel: ParcelInfo | null; // parcel currently being viewed in side panel
		isPogSplitVisible: boolean;
		isMpzpSplitVisible: boolean;
		isObszarUzupelnieniaZabudowySplitVisible: boolean;
		hoveredSplitZoneIndex: number | null;
		selectedParcels: Array<ParcelInfo>; // parcels selected for analysis
	};
};

const INITIAL_STATE: MapStoreState = {
	camera: {
		bearing: 0,
		pitch: 0,
		scaleDenominator: 0, // overwritten by the camera service as soon as the map exists
		projection: "perspective",
	},
	sources: {
		wms: new Map(),
		pmTiles: new Map(),
	},
	layers: {
		buildings3d: true,
		parcelLabels: false,
	},
	parcels: {
		filters: {
			parcel__pole_geometryczne: { kind: "range", min: null, max: null },
			parcel__grupa_rejestrowa: { kind: "set", values: [] },
			parcel__klasouzytki_egib: { kind: "set", values: [] },
			pog__symbol: { kind: "set", values: [] },
			pog__maksWysokoscZabudowy: { kind: "range", min: null, max: null },
			pog__maksNadziemnaIntensywnoscZabudowy: { kind: "range", min: null, max: null },
			pog__maksUdzialPowierzchniZabudowy: { kind: "range", min: null, max: null },
			pog__minUdzialPowierzchniBiologicznieCzynnej: { kind: "range", min: null, max: null },
			pog__pum: { kind: "range", min: null, max: null },
			pog__zPum: { kind: "flag", value: false },
			pog__bezPum: { kind: "flag", value: false },
			mpzp__strefa: { kind: "set", values: [] },
			mpzp__inArea: { kind: "flag", value: false },
			mpzp__pum: { kind: "range", min: null, max: null },
			mpzp__zPum: { kind: "flag", value: false },
			mpzp__bezPum: { kind: "flag", value: false },
			pum__diff: { kind: "range", min: null, max: null },
			parcel__zabytki: { kind: "flag", value: false },
			parcel__bezZabytkow: { kind: "flag", value: false },
			parcel__zabudowana: { kind: "flag", value: false },
			parcel__niezabudowana: { kind: "flag", value: false },
			obszar_uzupelnienia_zabudowy__inArea: { kind: "flag", value: false },
		},
		visualization: {
			heightField: null,
			heightFactor: 1,
			colorField: null,
			pumDiffColors: { pog: "#ed8c33", mpzp: "#1c3444" },
			heightDiffColors: { pog: "#ed8c33", mpzp: "#1c3444" },
			highlightParcelsOutsideMpzp: false,
		},
		visibleParcels: VisibleParcels.empty(),
		hoveredParcelId: null,
		activeParcel: null,
		isPogSplitVisible: false,
		isMpzpSplitVisible: false,
		isObszarUzupelnieniaZabudowySplitVisible: false,
		hoveredSplitZoneIndex: null,
		selectedParcels: [],
	},
};

function createStore() {
	return _createStore(subscribeWithSelector(() => INITIAL_STATE));
}

export type { MapStoreState };
export { createStore };
